import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { previewPages } from '../data/previews';
import { productShortName } from '../data/offer';
export function LookInsideCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const total = previewPages.length;
  const goTo = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const next = (index + total) % total;
    const slide = track.children[next] as HTMLElement | undefined;
    if (!slide) return;
    track.scrollTo({
      left: slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2,
      behavior: 'smooth'
    });
  }, [total]);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const center = track.scrollLeft + track.clientWidth / 2;
      let closest = 0;
      let distance = Infinity;
      Array.from(track.children).forEach((child, index) => {
        const el = child as HTMLElement;
        const d = Math.abs(el.offsetLeft + el.clientWidth / 2 - center);
        if (d < distance) {
          distance = d;
          closest = index;
        }
      });
      setActive(closest);
    };
    onScroll();
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', onScroll);
  }, []);
  return <section className="bg-slate-950 py-16 sm:py-20" aria-labelledby="inside-title" data-id="element-97" data-mp="26a2eog1jbvw9">
      <div className="mx-auto max-w-6xl px-4" data-id="element-98" data-mp="26c5bbix70n9y">
        <div className="text-center" data-id="element-99" data-mp="21a6c8l8hkcit">
          <p className="text-sm font-extrabold uppercase tracking-[.16em] text-yellow-400" data-id="element-100" data-mp="21b7xfij7ufe9">Look Inside</p>
          <h2 id="inside-title" className="mx-auto mt-3 max-w-4xl text-balance text-3xl font-extrabold uppercase leading-tight tracking-tight text-stone-50 sm:text-4xl" data-id="element-101" data-mp="1wdzf9hy2ibd0">
            Discover What&apos;s Inside <span className="text-yellow-400" data-id="element-102" data-mp="3qnt842hakfrq">{productShortName}</span>
          </h2>
          <span className="mx-auto mt-4 flex w-fit items-center gap-1" aria-hidden="true" data-id="element-103" data-mp="1ozfautfxpnkx">
            <span className="h-1 w-10 rounded-full bg-slate-500" data-id="element-104" data-mp="1tt1e5v07kf42" />
            <span className="h-1 w-3 rounded-full bg-yellow-400" data-id="element-105" data-mp="1f3bxerfmu2io" />
          </span>
          <p className="mx-auto mt-6 max-w-3xl leading-relaxed text-slate-300" data-id="element-106" data-mp="1k1dre7eljm8x">
            Visual pages with clear engine cut-aways, fuel, oil and coolant flow diagrams, generator set layouts, worked
            examples, safety warnings and field takeaways. Here are some real pages from Volume 1.
          </p>
        </div>

        <div className="relative mt-10" role="region" aria-roledescription="carousel" aria-label="Sample pages" data-id="element-107" data-mp="1ypi2oqbbkvho">
          <div ref={trackRef} className="flex touch-pan-x snap-x snap-mandatory gap-4 overflow-x-auto overscroll-x-contain scroll-smooth px-[10%] pb-2 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] sm:px-[20%] md:px-[35%] [&::-webkit-scrollbar]:hidden" data-id="element-108" data-mp="22qtxrbcyq8x9">
            {previewPages.map((page, index) => <figure key={page.src} className={`w-[80%] shrink-0 snap-center overflow-hidden rounded-2xl border bg-blue-950 shadow-lg shadow-black/40 transition-[border-color,opacity] duration-200 ease-out sm:w-[60%] md:w-[30%] ${index === active ? 'border-amber-400 opacity-100' : 'border-slate-600/40 opacity-70'}`} aria-roledescription="slide" aria-label={`${index + 1} of ${total}`} data-id="element-109" data-mp="jrvwqdg9cn12" data-mpi={index}>
                <img src={page.src} alt={page.alt} loading="lazy" draggable={false} className="block aspect-[4/5] w-full select-none object-cover" data-id="element-110" data-mp="13qy1kegjs94" />
              </figure>)}
          </div>

          <button type="button" onClick={() => goTo(active - 1)} className="absolute left-0 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-slate-500 bg-blue-950 text-stone-50 shadow-lg shadow-black/40 transition-colors duration-150 ease-out hover:bg-blue-900 focus:outline-none focus-visible:ring-4 focus-visible:ring-amber-400/50 sm:-left-2" aria-label="Previous page" data-id="element-111" data-mp="atu1kej7golp">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-112" data-mp="2csyt3aflmaj5" />
          </button>
          <button type="button" onClick={() => goTo(active + 1)} className="absolute right-0 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-slate-500 bg-blue-950 text-stone-50 shadow-lg shadow-black/40 transition-colors duration-150 ease-out hover:bg-blue-900 focus:outline-none focus-visible:ring-4 focus-visible:ring-amber-400/50 sm:-right-2" aria-label="Next page" data-id="element-113" data-mp="2gtsgm2p4pnbi">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-114" data-mp="30ff3kjr97pvy" />
          </button>
        </div>

        <div className="mt-6 flex justify-center gap-2" data-id="element-115" data-mp="34jczklla8kks">
          {previewPages.map((page, index) => <button key={page.src} type="button" onClick={() => goTo(index)} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400 ${index === active ? 'w-6 bg-yellow-400' : 'w-2 bg-slate-600 hover:bg-slate-500'}`} aria-label={`Go to page ${index + 1}`} aria-current={index === active} data-id="element-116" data-mp="39h4tfl5hmgrm" data-mpi={index} />)}
        </div>
      </div>
    </section>;
}